import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Avatar, AvatarGroup, Box, Button, Card, CardContent, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Stack, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import PlaceIcon from '@mui/icons-material/Place';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getEvent, joinSession, cancelJoin } from '../../api/eventApi';
import { useUser } from '../../context/userContext';
import SessionPlayer, { fmtWatched } from './sessionPlayer';
import EventChat from './eventChat';
import { fileUrl } from '../../utils/fileUrl';

const FONT = "'Poppins', sans-serif";

/**
 * One banner size for the whole app, taken from the dashboard's own: full width, 16:9,
 * and capped so it does not swallow a large screen. Cropped to fill rather than
 * stretched, so an image of any shape still reads correctly.
 * (user 2026-10-04: "unify a banner size", "need to be same as the dashboard banner")
 */
export const BANNER_SX = {
    width: '100%',
    aspectRatio: '16/9',
    maxHeight: { xs: 200, sm: 320, md: 480 },
    objectFit: 'cover',
    borderRadius: '14px',
    display: 'block',
};

const fmtWhen = (iso) => iso
    ? new Date(iso).toLocaleString('en-MY', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
    })
    : '';

/* ============================================================================
   THE TRAINING ROOM — one event, one session at a time

   Video on the left, chat on the right, the material and the attendee list
   underneath. Picking a different week changes both the player and the chat's
   default view in step.

   For an offline event there is no player: the card carries the venue, the map
   link and whatever the organiser uploaded afterwards.
   ========================================================================= */
export default function TrainingRoom() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();

    const [ev, setEv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getEvent(id);
            setEv(res.data);
        } catch {
            setEv(null);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Always the session running now or coming next - there is no date picker. A
    // training room is a room you walk into, not a catalogue of dates; earlier
    // sessions are reached through Edit Event, which is where the attendance for
    // them lives. (user 2026-10-03: "dont allow choose date please")
    const session = ev?.nextSession || (ev?.sessions || [])[0] || null;

    const attachments = useMemo(() => {
        if (!ev?.attachments) return [];
        try {
            const parsed = JSON.parse(ev.attachments);
            return Array.isArray(parsed) ? parsed.filter(a => a?.url) : [];
        } catch {
            return [];
        }
    }, [ev]);

    const booked = session?.myStatus && !['Cancelled', 'Declined'].includes(session.myStatus);

    const [confirmCancel, setConfirmCancel] = useState(false);
    // What the player has counted so far. The card used to read the figure that came
    // down with the page, so it sat frozen at page-load while the chip under the video
    // climbed - two numbers for one thing. They are the same number now.
    // (user 2026-10-04)
    const [liveStats, setLiveStats] = useState(null);

    const doCancel = async () => {
        if (!session) return;
        setBusy(true);
        setConfirmCancel(false);
        try {
            const res = await cancelJoin(session.eventSessionId);
            setNotice(res.data?.message || 'Your place has been released.');
            await load();
        } catch (e) {
            setNotice(e?.response?.data?.message || 'Could not release that place.');
        } finally {
            setBusy(false);
        }
    };

    const doJoin = async () => {
        if (!session) return;
        setBusy(true);
        try {
            const res = await joinSession(session.eventSessionId);
            setNotice(res.data?.message || 'Booked.');
            await load();
        } catch (e) {
            setNotice(e?.response?.data?.message || 'Could not book that.');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress size={28} /></Stack>;
    }

    if (!ev) {
        return (
            <Box sx={{ p: 3, maxWidth: 680, mx: 'auto' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>
                    Not available
                </Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.84rem', color: '#64748B', mb: 2 }}>
                    This event either does not exist or is not open to you.
                </Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')}
                    sx={{ fontFamily: FONT, textTransform: 'none' }}>
                    Back to events
                </Button>
            </Box>
        );
    }

    // A private event somebody was not invited to: title and date, nothing else.
    if (ev.isTeaser) {
        return (
            <Box sx={{ p: 3, maxWidth: 680, mx: 'auto' }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')}
                    sx={{ fontFamily: FONT, textTransform: 'none', mb: 2 }}>
                    Back to events
                </Button>
                <Card sx={{ borderRadius: '14px' }}>
                    <CardContent sx={{ py: 4, textAlign: 'center' }}>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.05rem', color: '#0F172A' }}>
                            {ev.title}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#64748B', mt: 0.5 }}>
                            {fmtWhen(ev.nextSession?.startsOn)}
                        </Typography>
                        <Chip label="By invitation" size="small"
                            sx={{ mt: 2, fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', color: '#94A3B8', mt: 2 }}>
                            Only the invitees can see the details for this one.
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')}
                sx={{ fontFamily: FONT, textTransform: 'none', mb: 1.5 }}>
                Back to events
            </Button>


            {/* The title and its chips, and nothing else. The brief used to sit here, and
                an event with a real agenda — a trainer line, four numbered topics, a
                sign-off — pushed the player and its countdown off the first screen, which
                is the thing people open this page for. The brief now reads under the
                player, where the banner already is. (user 2026-10-13: "the top only show
                new member onboarding training") */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.25rem', color: '#0F172A' }}>
                    {ev.title}
                </Typography>
                <Chip label={ev.type} size="small"
                    sx={{ height: 21, fontSize: '0.64rem', fontWeight: 700, bgcolor: '#EFF6FF', color: '#0E8FD1' }} />
                {ev.status !== 'Published' && (
                    <Chip label={ev.status} size="small"
                        sx={{ height: 21, fontSize: '0.64rem', fontWeight: 700, bgcolor: '#FEF3C7', color: '#92400E' }} />
                )}
                <Box sx={{ flex: 1 }} />
                {/* Edit from where you are looking at it, rather than going back out to
                    the list to find it again. Shown to whoever may manage the event -
                    Super Admin and Admin. (user 2026-10-03) */}
                {ev.canManage && (
                    <Button size="small" variant="outlined" startIcon={<EditIcon />}
                        onClick={() => navigate(`/events/${ev.eventId}/edit`)}
                        sx={{ fontFamily: FONT, textTransform: 'none', borderRadius: '8px', fontSize: '0.76rem' }}>
                        Edit event
                    </Button>
                )}
            </Stack>


            <Grid container spacing={2}>
                <Grid size={{ xs: 12, lg: 8 }}>
                    {ev.isOnline ? (
                        booked || ev.canManage ? (
                            <SessionPlayer
                                session={session}
                                clockSynced={ev.clockSynced}
                                isLive={ev.isLive}
                                onProgress={setLiveStats}
                            />
                        ) : (
                            <Card sx={{ borderRadius: '12px', bgcolor: '#F8FAFC' }}>
                                <CardContent sx={{ textAlign: 'center', py: 5 }}>
                                    <Typography sx={{ fontFamily: FONT, fontSize: '0.86rem', color: '#475569', mb: 2 }}>
                                        Register to join this session.
                                    </Typography>
                                    <Button variant="contained" onClick={doJoin} disabled={busy}
                                        sx={{ fontFamily: FONT, textTransform: 'none' }}>
                                        {busy ? 'Working…' : 'Register'}
                                    </Button>
                                </CardContent>
                            </Card>
                        )
                    ) : (
                        <Card sx={{ borderRadius: '12px' }}>
                            <CardContent>
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                                    <PlaceIcon sx={{ fontSize: 18, color: '#059669' }} />
                                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.9rem' }}>
                                        {ev.venue || 'Venue to be confirmed'}
                                    </Typography>
                                </Stack>
                                <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', color: '#475569' }}>
                                    {fmtWhen(session?.startsOn)}
                                </Typography>
                                {ev.venueMapUrl && (
                                    <Button size="small" href={ev.venueMapUrl} target="_blank" rel="noreferrer"
                                        sx={{ fontFamily: FONT, textTransform: 'none', mt: 1 }}>
                                        Open the map
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* The brief, directly under the player. Pre-wrapped because people
                        type it as a list and the line breaks are the formatting. */}
                    {ev.description && (
                        <Typography sx={{
                            fontFamily: FONT, fontSize: '0.84rem', color: '#64748B',
                            whiteSpace: 'pre-wrap', mt: 2, maxWidth: 820,
                        }}>
                            {ev.description}
                        </Typography>
                    )}

                    {/* One place on every event, video or not: under the card that carries
                        the point of the page - the player on a live session, the venue and
                        date on one that is not. It began above the title for non-video
                        events; matching the video layout reads better and means there is
                        only one rule to remember. (user 2026-10-04: "this one non video i
                        think follow the video placement is better. put below the cards") */}
                    {ev.bannerUrl && (
                        <Box component="img" src={fileUrl(ev.bannerUrl)} alt=""
                            sx={{ ...BANNER_SX, mt: 2 }} />
                    )}

                    {attachments.length > 0 && (
                        <Card sx={{ borderRadius: '12px', mt: 2 }}>
                            <CardContent>
                                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.86rem', mb: 1 }}>
                                    Material
                                </Typography>
                                <Stack spacing={0.75}>
                                    {attachments.map((a, i) => (
                                        <Button key={i} startIcon={<DownloadIcon />} href={fileUrl(a.url)} target="_blank" rel="noreferrer"
                                            sx={{ fontFamily: FONT, textTransform: 'none', justifyContent: 'flex-start', fontSize: '0.8rem' }}>
                                            {a.name || `Attachment ${i + 1}`}
                                        </Button>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    <Card sx={{ borderRadius: '12px', mt: 2 }}>
                        <CardContent>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                                <GroupIcon sx={{ fontSize: 17, color: '#64748B' }} />
                                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.86rem' }}>
                                    {session?.joinedCount || 0} going
                                </Typography>
                                {session?.seatsLeft != null && (
                                    <Chip label={`${session.seatsLeft} seats left`} size="small"
                                        sx={{ height: 19, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#F1F5F9', color: '#475569' }} />
                                )}
                            </Stack>
                            {(session?.attendees || []).length > 0 ? (
                                <AvatarGroup
                                    max={12}
                                    sx={{
                                        // Faces read left to right like everything else on the
                                        // page; they were pinned to the right, floating away
                                        // from the count they belong to. (user 2026-10-04)
                                        justifyContent: 'flex-start', flexDirection: 'row',
                                        '& .MuiAvatar-root': {
                                            width: 30, height: 30, fontSize: '0.7rem',
                                            fontFamily: FONT, fontWeight: 700,
                                        },
                                    }}
                                >
                                    {session.attendees.map(a => (
                                        <Tooltip key={a.userId} title={a.name}>
                                            <Avatar src={fileUrl(a.profilePhotoUrl)} alt={a.name}>
                                                {(a.name || '?').charAt(0)}
                                            </Avatar>
                                        </Tooltip>
                                    ))}
                                </AvatarGroup>
                            ) : (
                                <Typography sx={{ fontFamily: FONT, fontSize: '0.76rem', color: '#94A3B8' }}>
                                    No registrations yet
                                </Typography>
                            )}

                            <Divider sx={{ my: 1.5 }} />
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                                {booked ? (
                                    // Cancelling lives HERE and not on the list row: the row was
                                    // where it cluttered (user 2026-10-03: "remove release my
                                    // place"), but inside the event, with the date in front of
                                    // you, dropping out has to be possible - otherwise a booking
                                    // is permanent and only a developer can undo it.
                                    // (user 2026-10-04)
                                    <>
                                        <Chip
                                            label={session.myStatus === 'Attended' ? 'Attended' : 'Registered'}
                                            size="small"
                                            icon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
                                            sx={{
                                                fontFamily: FONT, fontWeight: 700, fontSize: '0.74rem',
                                                bgcolor: '#DCFCE7', color: '#065F46',
                                                '& .MuiChip-icon': { color: '#059669' },
                                            }} />
                                        {/* Pushed to the far side: a quiet, deliberate action,
                                            never sitting right beside the badge where it could
                                            be hit by accident. (user 2026-10-04) */}
                                        <Box sx={{ flex: 1 }} />
                                        {session.myStatus !== 'Attended' && (
                                            <Button size="small" variant="text" onClick={() => setConfirmCancel(true)} disabled={busy}
                                                sx={{
                                                    fontFamily: FONT, textTransform: 'none', fontSize: '0.74rem',
                                                    color: '#94A3B8', '&:hover': { color: '#B45309', bgcolor: '#FFF7ED' },
                                                }}>
                                                Cancel my place
                                            </Button>
                                        )}
                                    </>
                                ) : ev.isOnline && !ev.canManage ? (
                                    // The big card above already asks them to book - this row
                                    // was offering the same button a second time, a few
                                    // centimetres below the first. (user 2026-10-04)
                                    <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', color: '#94A3B8' }}>
                                        Register above to join.
                                    </Typography>
                                ) : (
                                    <Button variant="contained" size="small" onClick={doJoin} disabled={busy}
                                        sx={{ fontFamily: FONT, textTransform: 'none' }}>
                                        {busy ? 'Working…' : ev.requiresApproval ? 'Request a place' : 'Register'}
                                    </Button>
                                )}
                                {notice && (
                                    <Typography sx={{ fontFamily: FONT, fontSize: '0.74rem', color: '#0E8FD1' }}>{notice}</Typography>
                                )}
                            </Stack>

                            <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)}>
                                <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1rem' }}>
                                    Cancel your place
                                </DialogTitle>
                                <DialogContent>
                                    <Typography sx={{ fontFamily: FONT, fontSize: '0.86rem', color: '#475569' }}>
                                        {ev.title} — {fmtWhen(session?.startsOn)}.
                                        {ev.capacity ? ' Your seat goes to the next person waiting.' : ''}
                                        {' '}You can book again while there is room.
                                    </Typography>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setConfirmCancel(false)}
                                        sx={{ fontFamily: FONT, textTransform: 'none' }}>
                                        Cancel
                                    </Button>
                                    <Button onClick={doCancel} disabled={busy} variant="contained" color="warning"
                                        sx={{ fontFamily: FONT, textTransform: 'none' }}>
                                        Cancel my place
                                    </Button>
                                </DialogActions>
                            </Dialog>

                            {booked && ((liveStats?.watchedSeconds ?? session?.myWatchedSeconds) > 0) && (
                                <Typography sx={{ fontFamily: FONT, fontSize: '0.74rem', color: '#64748B', mt: 1 }}>
                                    You have watched {fmtWatched(liveStats?.watchedSeconds ?? session.myWatchedSeconds)} of this session
                                    {(liveStats?.completionPercent ?? session.myCompletionPercent)
                                        ? ` (${liveStats?.completionPercent ?? session.myCompletionPercent}%)` : ''}.
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* The chat follows you down the page instead of running off the bottom of
                    it. It used to be as tall as the video column, so on a long page the
                    message box ended up far below whatever you were reading and you had to
                    scroll back to it to press Send. Now the column is capped to the
                    viewport, the messages scroll inside it, and the box stays put.
                    Sticky only from lg up - below that the chat sits under the video and
                    there is nothing to stick to. (user 2026-10-04) */}
                <Grid size={{ xs: 12, lg: 4 }}
                    sx={{
                        position: { lg: 'sticky' },
                        top: { lg: 80 },
                        alignSelf: { lg: 'flex-start' },
                    }}>
                    <Card sx={{
                        borderRadius: '12px', display: 'flex', flexDirection: 'column',
                        maxHeight: { xs: '70vh', lg: 'calc(100vh - 104px)' },
                    }}>
                        <CardContent sx={{
                            flex: 1, display: 'flex', flexDirection: 'column',
                            minHeight: 0, overflow: 'hidden',
                        }}>
                            <EventChat
                                eventId={ev.eventId}
                                eventSessionId={session?.eventSessionId}
                                canManage={ev.canManage}
                                meId={user?.userId}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}
